import { useDeviceType } from "@dash/mq";
import { Page, PageHeader } from "@dash/page";
import { SearchWithPaginationSticky } from "@dash/search";
import { Spinner } from "@dash/spinner";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom-v5-compat";

import { NavAccentThemeScope } from "../nav/NavAccentThemeScope";
import { AllSignalsFilters, appOptionsFromSignals, signalTypeOptionsFromSignals } from "./AllSignalsFilters";
import { AllSignalsList } from "./AllSignalsList";
import { AllSignalsTable } from "./AllSignalsTable";
import { filterSignals, SignalsFilterValues } from "./filterSignals";
import { groupSignals, SignalDocumentGroup, sortSignalGroups } from "./groupSignals";
import { signalTypeDisplayLabel } from "./signalDisplayLabel";
import { matchesSignalReadFilter } from "./signalReadStatus";
import { useSignals } from "./SignalsProvider";
import { useAllSignalsFilters } from "./useAllSignalsFilters";

const pageSize = 25;

export const AllSignalsPage: React.FC = () => {
  const { isMobile } = useDeviceType();
  const navigate = useNavigate();
  const { signals, isLoading, isError, refetch, markRead } = useSignals();
  const { filters, setFilters } = useAllSignalsFilters();
  const page = filters.page;

  const appOptions = useMemo(
    () => appOptionsFromSignals([...new Set(signals.map((signal) => signal.app))].sort()),
    [signals],
  );

  const signalTypeOptions = useMemo(
    () =>
      signalTypeOptionsFromSignals(
        [...new Set(signals.map((signal) => signal.signalType))].sort(),
        signalTypeDisplayLabel,
      ),
    [signals],
  );

  const filteredGroups = useMemo(() => {
    const resolveAppLabel = (app: string) => appOptions.find((option) => option.value === app)?.label ?? app;
    const filtered = filterSignals(signals, { ...filters, sort: undefined }, resolveAppLabel);
    const grouped = groupSignals(filtered).filter((group) => matchesSignalReadFilter(group, filters.isUnread));
    return sortSignalGroups(grouped, filters.sort, resolveAppLabel);
  }, [signals, filters, appOptions]);

  const pageItems = filteredGroups.slice(page * pageSize, page * pageSize + pageSize);

  const updateFilters = (patch: Partial<SignalsFilterValues>) => {
    setFilters({ ...filters, ...patch, page: 0 });
  };

  const onPageChange = (nextPage: number) => {
    setFilters({ ...filters, page: nextPage });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleRowNavigate = (href: string, group: SignalDocumentGroup) => {
    markRead(group.signals.map((signal) => signal.docId));
    navigate(href);
  };

  return (
    <Page
      css={{
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
        maxWidth: "100%",
        minWidth: 0,
        overflowX: "hidden",
        paddingBottom: "2rem",
      }}
    >
      <NavAccentThemeScope>
        <PageHeader>Powiadomienia</PageHeader>

        <SearchWithPaginationSticky
          searchValue={filters.searchText}
          onSearchChange={(searchText) => updateFilters({ searchText })}
          page={page}
          onPageChange={onPageChange}
          pageSize={pageSize}
          itemCount={filteredGroups.length}
        />

        <AllSignalsFilters
          values={filters}
          appOptions={appOptions}
          signalTypeOptions={signalTypeOptions}
          onChange={updateFilters}
        />
      </NavAccentThemeScope>

      {isError ? (
        <span css={{ opacity: 0.6, padding: "1rem 0" }}>
          Nie udało się wczytać powiadomień.{" "}
          <button
            type="button"
            onClick={() => void refetch()}
            css={{ background: "none", border: "none", padding: 0, textDecoration: "underline", cursor: "pointer" }}
          >
            Spróbuj ponownie
          </button>
        </span>
      ) : isLoading ? (
        <Spinner size="1.5rem" css={{ margin: "1rem auto" }} />
      ) : signals.length === 0 ? (
        <span css={{ opacity: 0.6, padding: "1rem 0" }}>Brak powiadomień.</span>
      ) : filteredGroups.length === 0 ? (
        <span css={{ opacity: 0.6, padding: "1rem 0" }}>Brak wyników.</span>
      ) : isMobile ? (
        <AllSignalsList items={pageItems} onRowNavigate={handleRowNavigate} />
      ) : (
        <AllSignalsTable items={pageItems} onRowNavigate={handleRowNavigate} />
      )}
    </Page>
  );
};
